import React, { memo, useCallback, useContext, useMemo } from "react";

// components
import { ErrorBoundary } from "@swiggy-private/core-ui";
import {
    SubscriptionRenewalNudge,
    SubscriptionRenewalNudgeProps,
    SubscriptionRenewalHandlers,
    FlowType,
} from "@swiggy-private/super-ui";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useOneMembershipPlanHandlers } from "@cart-client/hooks/useOneMembershipPlanHandlers";

// helpers
import { getNRErrorInfo, getNestedValue, isEmpty } from "@swiggy-private/js-utils";
import { getUserSuperStatus, navigateToDeeplinkGenericHelper } from "@cart-client/helpers/Cart";

// constants

// types
import { PresentationInfo, UserSuperStatus } from "@swiggy-private/cart-types";
import { PLAN_ADDITION_SOURCE } from "@cart-client/constants/index";
import { useSubscriptionRenewalAnalytics } from "@cart-client/hooks/useSubscriptionRenewalAnalytics";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";

const SubscriptionRenewalWidget: React.FC<{ isTopPositioned?: boolean }> = ({ isTopPositioned = false }) => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;

    const { addSuperToCart, removeSuperFromCart } = useOneMembershipPlanHandlers();

    const removeSuperCallback = useCallback(() => {
        removeSuperFromCart(true);
    }, [removeSuperFromCart]);

    const swiggyPlanInfo: PresentationInfo = useMemo(
        () => getNestedValue(cartResponse, "data.swiggyOneInfo.presentationInfo"),
        [cartResponse],
    );

    const addSuperCallback = useCallback(
        (planId: string, planType?: string) => {
            addSuperToCart(planId, FlowType.COMPARISON, planType);
        },
        [addSuperToCart],
    );

    const onError = useCallback(
        (error?: Error, errorInfo?: React.ErrorInfo) => {
            const errorEvents = getNRErrorInfo(error, errorInfo);

            recordNREvent({
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
                widgetType: "SUBSCRIPTION_RENEWAL_WIDGET",
                ...errorEvents,
                nudgeComms: JSON.stringify(swiggyPlanInfo?.comparison_nudge_comms),
            });
        },
        [swiggyPlanInfo?.comparison_nudge_comms],
    );

    const handlers: SubscriptionRenewalHandlers = useMemo(
        () => ({
            navigateToDeeplinkGenericHelper,
            addSuperToCart: addSuperCallback,
            removeSuperFromCart: removeSuperCallback,
        }),
        [addSuperCallback, removeSuperCallback],
    );

    const subscriptionRenewalAnalytics = useSubscriptionRenewalAnalytics({
        header: swiggyPlanInfo?.comparison_nudge_comms?.header || "",
        variant: swiggyPlanInfo?.comparison_nudge_comms?.variant || "",
        userSuperStatus: getUserSuperStatus(cartResponse) as UserSuperStatus,
        source: PLAN_ADDITION_SOURCE.SUBSCRIPTION_RENEWAL_NUDGE,
    });

    const props: SubscriptionRenewalNudgeProps = useMemo(
        () => ({
            comparisonNudgeComms: swiggyPlanInfo?.comparison_nudge_comms,
            handlers,
            analyticsEvents: { subscriptionRenewalAnalytics },
            removePlanMessage: swiggyPlanInfo?.remove_plan_message,
            planDetails: swiggyPlanInfo?.plan_details,
            isTopPositioned,
        }),
        [
            swiggyPlanInfo?.comparison_nudge_comms,
            swiggyPlanInfo?.remove_plan_message,
            swiggyPlanInfo?.plan_details,
            handlers,
            subscriptionRenewalAnalytics,
            isTopPositioned,
        ],
    );

    // Early return if no data to render
    if (isEmpty(swiggyPlanInfo?.comparison_nudge_comms?.nudges)) {
        return null;
    }

    return (
        <ErrorBoundary errorContent={null} errorFallback={onError}>
            <SubscriptionRenewalNudge {...props} />
        </ErrorBoundary>
    );
};

export default memo(SubscriptionRenewalWidget);